'use client';

import { useState } from 'react';
import { useCart } from '@akademi/site-builder-runtime';
import { formatMoney } from '@/src/lib/money';
import { onBasis, type PricedMoney } from '@/src/lib/vatDisplay';
import type { QuantityPrice } from '@/src/lib/commerce/wire';
import { useVatDisplay } from './VatDisplay';

/**
 * Buy more, pay less: the unit price from each quantity upwards, and a one-click full pallet.
 *
 * <p>Every figure is one the backend priced at that quantity — the same read path the basket
 * uses — so the table cannot promise a price the basket will not charge. Nothing is computed
 * here but the saving as a share of the single-unit price, which is a ratio of two published
 * figures on the same tax basis, never a tax.
 *
 * <p>Both parts render only from data: no breaks, no table; no pallet quantity, no pallet
 * offer. A shop whose catalogue has neither sees nothing, not an empty frame.
 *
 * <p>The `data-multi-buy*` attributes are hooks for a shop's theme, like `sf-plp*`.
 */
export function MultiBuy({
  productId,
  unitPrice,
  quantityPrices,
  palletQty,
  locale,
  labels,
  outOfStock = false,
  part = 'both',
}: {
  productId: number;
  unitPrice?: PricedMoney;
  quantityPrices?: QuantityPrice[];
  palletQty?: number;
  locale: string;
  labels: {
    heading: string;
    quantity: string;
    unitPrice: string;
    from: string;
    save: string;
    each: string;
    pallet: string;
    palletUnits: string;
    addPallet: string;
    added: string;
    error: string;
  };
  outOfStock?: boolean;
  /** Which half to draw — the product page puts the table above its quantity box and the
   *  pallet offer below it. */
  part?: 'table' | 'pallet' | 'both';
}) {
  const { basis } = useVatDisplay();
  const cart = useCart();
  const [state, setState] = useState<'idle' | 'busy' | 'added' | 'failed'>('idle');
  const rows = part === 'pallet' ? [] : (quantityPrices ?? []);
  // A pallet is offered only where there is a basket to put it in: a shop that sells by
  // enquiry has none, and an inert button is worse than no offer.
  const pallet = part === 'table' || !cart?.available ? undefined : palletQty;
  if (!rows.length && !pallet) return null;

  const base = unitPrice ? onBasis(unitPrice, basis) : undefined;
  const savingOf = (price: PricedMoney): string | null => {
    const shown = onBasis(price, basis);
    const one = Number(base?.amount);
    const at = Number(shown.amount);
    if (!base || shown.taxInclusive !== base.taxInclusive || !(one > 0) || !(at < one)) return null;
    const pct = (1 - at / one) * 100;
    return new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(pct);
  };
  const format = (price: PricedMoney) => formatMoney(onBasis(price, basis), locale);
  const atPallet = palletQty
    ? [...(quantityPrices ?? [])].reverse().find((row) => row.minQty <= palletQty)
    : undefined;
  const count = (value: number) => new Intl.NumberFormat(locale).format(value);

  async function addPallet() {
    if (!cart || !palletQty) return;
    setState('busy');
    try {
      await cart.add({ productId, quantity: String(palletQty) });
      setState('added');
    } catch {
      setState('failed');
    }
  }
  // The cart reports its own failures on `error`, as BasketStepper reads them; the value
  // captured before the await would be the one from before this add.
  const failed = state === 'failed' || (state === 'added' && !!cart?.error);

  return (
    <div data-multi-buy className="flex flex-col gap-3">
      {rows.length ? (
        <table data-multi-buy-table className="w-full border-collapse text-sm">
          <caption className="mb-1 text-left text-xs font-semibold uppercase tracking-wide text-[var(--color-text-muted)]">
            {labels.heading}
          </caption>
          <thead className="sr-only">
            <tr>
              <th scope="col">{labels.quantity}</th>
              <th scope="col">{labels.unitPrice}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const saving = savingOf(row.price);
              return (
                <tr key={row.minQty} className="border-t border-[var(--color-border-subtle)]">
                  <td className="py-1.5 pr-2">
                    {labels.from.replace('{qty}', count(row.minQty))}
                  </td>
                  <td className="py-1.5 text-right tabular-nums">
                    <span className="font-semibold">{format(row.price)}</span>{' '}
                    <span className="text-[var(--color-text-muted)]">{labels.each}</span>
                    {saving ? (
                      <span data-multi-buy-saving className="ml-2 font-semibold">
                        {labels.save.replace('{pct}', saving)}
                      </span>
                    ) : null}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      ) : null}

      {pallet && cart ? (
        <div
          data-multi-buy-pallet
          className="flex flex-wrap items-center justify-between gap-2 rounded-md bg-[var(--color-surface-muted)] px-3 py-2 text-sm"
        >
          <span>
            <span className="font-semibold">{labels.pallet}</span>{' '}
            {labels.palletUnits.replace('{qty}', count(pallet))}
            {atPallet ? <> · {format(atPallet.price)} {labels.each}</> : null}
          </span>
          <button
            type="button"
            onClick={() => void addPallet()}
            disabled={outOfStock || state === 'busy' || cart.busy}
            className="min-h-9 rounded-md border border-[var(--color-border-strong,var(--color-border-subtle))] bg-[var(--color-surface-card)] px-3 font-semibold disabled:opacity-55 focus-visible:outline-2 focus-visible:outline-offset-2"
          >
            {labels.addPallet}
          </button>
          <span role="status" aria-live="polite" className="w-full text-xs">
            {failed ? labels.error : state === 'added' ? labels.added : null}
          </span>
        </div>
      ) : null}
    </div>
  );
}
